"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";
import Image from "next/image";

/* ─── Scroll reveal hook ──────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Reveal wrapper ──────────────────────────────────────── */
function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const base = "transition-all duration-700 ease-out";
  const hidden = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`${base} ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Check icon ──────────────────────────────────────────── */
function CheckIcon() {
  return (
    <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/* ─── Section label / eyebrow ─────────────────────────────── */
type EyebrowProps = { text: string };

function Eyebrow({ text }: EyebrowProps) {
  return (
    <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">
      {text}
    </span>
  );
}

function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}

/* ─── Data ────────────────────────────────────────────────── */
const SETUP_SERVICES = [
  { title: "Private Limited Company Registration", detail: "MCA incorporation, digital signature, DIN, MOA, AOA, PAN, TAN, bank account opening support" },
  { title: "LLP Registration",                     detail: "LLP agreement, designated partner setup, DPIN, registration certificate, PAN/TAN" },
  { title: "OPC (One Person Company)",             detail: "Sole-director company structure with full compliance setup" },
  { title: "MSME / Udyam Registration",            detail: "Government MSME recognition — unlocks loans, subsidies and government tenders" },
  { title: "Startup India (DPIIT) Recognition",   detail: "DPIIT certificate + access to tax exemptions, funding and government programs" },
  { title: "GST Registration",                     detail: "GST registration, HSN code mapping and compliance setup" },
  { title: "GeM Registration",                     detail: "Government eMarketplace setup to sell directly to government buyers" },
  { title: "Tax Exemption Certificates",           detail: "80-IAC (startup tax exemption), 12AB, 80G for non-profits" },
  { title: "Professional Tax Registration",        detail: "State-specific professional tax registration for employers" },
  { title: "Import Export Code (IEC)",             detail: "IEC registration for businesses involved in international trade" },
];

const LAND_FEATURES = [
  "Industrial plots in MIDC, GIDC, RIICO, SIPCOT and other state industrial zones",
  "Commercial spaces in Tier 1, Tier 2 and Tier 3 cities",
  "Warehouse and logistics hubs near major transport corridors",
  "Agricultural land for agri-processing and food businesses",
  "Verified titles, clean documentation and ready-to-register properties",
];

const JOURNEY_STEPS = [
  { n: "01", title: "Business Planning",   body: "Strategic planning, market analysis and business model validation to define your roadmap." },
  { n: "02", title: "Registration",         body: "Company registration, licensing and all statutory registrations — handled end to end." },
  { n: "03", title: "Compliance Setup",     body: "Tax, labour law and regulatory compliance framework established from day one." },
  { n: "04", title: "Funding Assistance",   body: "Access to government grants, MSME loans, NBFC financing and investor networks." },
  { n: "05", title: "Marketing Strategy",   body: "Go-to-market planning, brand strategy and digital marketing to acquire your first customers." },
  { n: "06", title: "Growth & Scaling",     body: "Expansion strategy, new market entry, technology adoption and ongoing business advisory." },
];

const FAQS = [
  { q: "What does 360° support include?",         a: "Our 360° support covers business registration, compliance, funding assistance, marketing strategy and growth planning — all under one roof. You get a single point of contact for every business need." },
  { q: "How long does business registration take?",a: "Typical registration takes 7–10 working days depending on the business type and document completeness. We manage the entire process so you can focus on your business." },
  { q: "Can you help with post-registration compliance?", a: "Yes. We provide ongoing compliance management including GST filing, annual returns, labour law advisory and regulatory updates — so you never miss a deadline." },
  { q: "Do you assist with funding after registration?",  a: "Yes. We help you access government grants, MSME loans, NBFC financing and connect you with angel and institutional investors at the right stage." },
  { q: "Can I get land or premises support through Farsight?", a: "Yes. We provide access to verified industrial, commercial, warehouse and agricultural land across 20+ states — with clean documentation and ready-to-register properties." },
  { q: "Is there a free consultation available?",   a: "Yes. We offer a completely free, no-obligation consultation to assess your needs and guide you through the right services in the right sequence — no upselling, no jargon." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
type FaqItemProps = { q: string; a: string; i: number };

function FaqItem({ q, a, i }: FaqItemProps) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="font-roboto text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Setup Service Row ───────────────────────────────────── */
type ServiceRowProps = { svc: { title: string; detail: string }; i: number };

function ServiceRow({ svc, i }: ServiceRowProps) {
  const [ref, v] = useVisible<HTMLTableRowElement>();
  return (
    <tr
      ref={ref}
      className={`transition-all duration-500 ${v ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"} ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} border-b border-slate-200`}
      style={{ transitionDelay: `${(i % 5) * 0.07}s` }}
    >
      <td className="px-5 py-4 align-top">
        <div className="flex items-start gap-2.5">
          <div className="w-1.5 h-1.5 bg-blue-700 flex-shrink-0 mt-2" />
          <span className="text-[13px] font-bold text-slate-900 leading-snug">{svc.title}</span>
        </div>
      </td>
      <td className="px-5 py-4 text-[13px] text-slate-500 leading-relaxed align-top">{svc.detail}</td>
    </tr>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
type StepProps = { s: { n: string; title: string; body: string }; i: number; last: boolean };

function Step({ s, i, last }: StepProps) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"} ${!last ? "pb-7" : ""} relative`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s` }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white tracking-wide">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function Support360Page() {
  return (
    <main className="bg-white font-roboto">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">
        {/* Left: text */}
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-10 lg:py-24 bg-white">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <span>360° Business Support</span>
          </nav>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["Registration", "Compliance", "Funding", "Land & Infrastructure", "Growth"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">
                {tag}
              </span>
            ))}
          </div>

          <Eyebrow text="Complete Business Support" />
          <Divider />

          <h1 className="text-[clamp(28px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-6 ">
            Start Strong. Grow Strategically. Stay Future-Ready.
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Whether you are launching a new venture or scaling an established business, Farsight Solutions provides complete 360° support — from your first registration to your next crore in revenue, all under one roof.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors cursor-pointer border-0">
              GET FREE CONSULTATION
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              WHATSAPP OUR EXPERTS
            </button>
          </div>
        </div>

        {/* Right: photo */}
        <div className="relative overflow-hidden">
          <Image
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=85"
            alt="360 business support"
            fill
            className="object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "10+",   l: "Registration Types" },
          { v: "100+",  l: "Clients Supported" },
          { v: "20+",   l: "States Covered" },
          { v: "Free",  l: "First Consultation" },
        ].map((s, i) => (
          <div
            key={i}
            className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}
          >
            <p className="text-[24px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ BUSINESS SETUP SERVICES — table + image ════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Business Setup Services" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Start Right. Stay Compliant.<br />Grow with Confidence.
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              We simplify the business setup process by handling everything from company registration to essential certifications. Our experts ensure your business is structured correctly and fully compliant from day one.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-200 pt-7 gap-0">
              {[{ v: "10+", l: "Registration Types" }, { v: "7–10", l: "Days to Register" }, { v: "100%", l: "Managed End-to-End" }].map((s, i) => (
                <div key={i} className={`${i > 0 ? "pl-6 border-l border-slate-200" : ""}`}>
                  <p className="text-[28px] font-black text-blue-700 leading-none">{s.v}</p>
                  <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[240px] sm:h-[320px] lg:h-[400px]">
                <Image
                  src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&q=80"
                  alt="Business registration"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute bottom-[-28px] right-[-24px] w-1/2 h-[190px]">
                <Image
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&q=80"
                  alt="Team collaboration"
                  fill
                  className="object-cover border-[5px] border-white shadow-xl"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services table */}
      <section className="pb-12 sm:pb-16 lg:pb-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-700">
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase w-[38%]">Service</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase">What&apos;s Included</th>
                </tr>
              </thead>
              <tbody>
                {SETUP_SERVICES.map((svc, i) => <ServiceRow key={i} svc={svc} i={i} />)}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* ══ BUSINESS JOURNEY — steps + image ═══════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Your Business Journey" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              6-Stage Roadmap to Business Success
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A structured, guided journey from idea to scale — with Farsight managing every stage so you can focus on building your business.
            </p>
            <div>
              {JOURNEY_STEPS.map((s, i) => (
                <Step key={i} s={s} i={i} last={i === JOURNEY_STEPS.length - 1} />
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative w-full h-[300px] sm:h-[450px] lg:h-[600px] sticky top-24">
              <Image
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80"
                alt="Business journey"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ LAND & INFRASTRUCTURE ══════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <div className="relative">
              <div className="relative w-full h-[260px] sm:h-[360px] lg:h-[460px]">
                <Image
                  src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80"
                  alt="Business land infrastructure"
                  fill
                  className="object-cover"
                />
              </div>
              {/* Floating stat */}
              <div className="hidden lg:block absolute bottom-[-24px] right-[-24px] bg-white border border-slate-200 shadow-xl px-6 py-5">
                <p className="text-[30px] font-black text-blue-700 leading-none">20+</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">States Covered</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">Verified</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Clean Titles Only</p>
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="Business Land & Infrastructure" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Affordable Business Land Across India
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Access verified industrial, commercial, agricultural and storage-ready land opportunities across 20+ states in India — to support your business setup, expansion, or manufacturing needs.
            </p>
            <div className="flex flex-col gap-3.5">
              {LAND_FEATURES.map((pt, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckIcon />
                  <span className="text-[13.5px] text-slate-600 leading-relaxed">{pt}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About 360° Business Support
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Everything you need to know before starting your business journey with Farsight Solutions.
            </p>
            <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px]">
              <Image
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&q=80"
                alt="Business support FAQ"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden">
              <video
                src="/videos/CTA.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Still Confused? We&apos;ve Got You." />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Get a Free Business Consultation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Starting or scaling a business is complex. That is why our experts are available for a free, no-pressure consultation — to guide you through exactly what you need and in what order.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              No upselling, no jargon. Just honest, practical guidance from people who have helped 100+ Indian businesses launch and grow.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors cursor-pointer border-0">
                GET FREE CONSULTATION
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                WHATSAPP OUR EXPERTS
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}